/**
 * CLO Card Add Functionality
 *
 * Handles adding Card Linked Offer cards to the user's card.
 * Once added, an offer cannot be removed (one-way action).
 * Posts to /offers/{offerId}/clip to enroll the member in the coupon program.
 */

const CLO_CARD_SELECTOR = '[data-clo-card]';
const CLO_ADD_BTN_SELECTOR = '[data-clo-add-btn]';
const CLO_ADDED_CLASS = 'ox-clo-card--added';
const CLO_BADGE_ENTER_CLASS = 'ox-clo-card--badge-enter';
const CLO_CLIPPED_BADGE_SELECTOR = '.ox-clo-card__clipped-badge';
const CLO_CTA_SELECTOR = '.ox-clo-card__cta';

function getCSRFToken(): string {
    const tokenElem = document.getElementById('token');
    return tokenElem?.getAttribute('data-token') ?? '';
}

/**
 * Disables the clip button and changes its text to "Already redeemed".
 * This mirrors the server-rendered markup for the added/redeemed state.
 */
function showRedeemedState(card: HTMLElement): void {
    card.classList.add(CLO_ADDED_CLASS);
}

/**
 * Adds the enter-animation class for the clipped badge, then removes it when finished.
 * This keeps server-rendered "already added" cards from animating on page load.
 */
function animateClippedBadge(card: HTMLElement): void {
    const badge = card.querySelector<HTMLElement>(CLO_CLIPPED_BADGE_SELECTOR);
    if (!badge) return;

    // Reset first so repeated success states can restart the animation reliably.
    card.classList.remove(CLO_BADGE_ENTER_CLASS);
    // Force reflow to ensure class re-add triggers keyframes.
    void card.offsetWidth;
    card.classList.add(CLO_BADGE_ENTER_CLASS);

    const handleAnimationEnd = (): void => {
        card.classList.remove(CLO_BADGE_ENTER_CLASS);
    };

    badge.addEventListener('animationend', handleAnimationEnd, { once: true });
}

/**
 * Clips an offer by POSTing to /offers/{offerId}/clip
 */
async function clipOffer(
    card: HTMLElement,
    btn: HTMLButtonElement
): Promise<void> {
    const offerId = btn.dataset['cloOfferId'];
    if (!offerId) return;

    // Prevent double-clicks
    btn.disabled = true;

    try {
        const response = await fetch(`/offers/${offerId}/clip`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCSRFToken()
            }
        });

        if (response.ok) {
            showRedeemedState(card);
            animateClippedBadge(card);
            showFirstClipDialog();
        } else if (response.status === 409) {
            // 409 = already clipped — still transition to redeemed state
            showRedeemedState(card);
        } else {
            btn.disabled = false;
        }
    } catch {
        btn.disabled = false;
    }
}

const FIRST_CLIP_DIALOG_ID = 'cloFirstClipDialog';
const FIRST_CLIP_DIALOG_PROP = `${FIRST_CLIP_DIALOG_ID}-openDialog`;

/**
 * Shows the first-clip onboarding dialog if it exists in the DOM,
 * then removes it after close so it only fires once per page load.
 */
function showFirstClipDialog(): void {
    const handler = (window as any)[FIRST_CLIP_DIALOG_PROP];
    if (!handler?.show) return;

    handler.show();

    const dialogEl = document.getElementById(FIRST_CLIP_DIALOG_ID);
    if (dialogEl) {
        dialogEl.addEventListener(
            'close',
            () => {
                dialogEl.remove();
                delete (window as any)[FIRST_CLIP_DIALOG_PROP];
                document.cookie =
                    'clo_first_clip_seen=1; path=/; max-age=31536000; SameSite=Lax';
            },
            { once: true }
        );
    }
}

const SMS_OPT_IN_BTN_SELECTOR = '[data-sms-opt-in-btn]';
const SMS_ALERT_SELECTOR = '.ox-first-clip-sms-alert';

/**
 * Attaches a click handler to the inline SMS opt-in button inside the
 * first-clip dialog. On success the server returns the success-state
 * HTML fragment which replaces the widget in-place.
 */
function initSMSOptIn(): void {
    const btn = document.querySelector<HTMLButtonElement>(
        SMS_OPT_IN_BTN_SELECTOR
    );
    if (!btn || btn.dataset['smsInitialized']) return;
    btn.dataset['smsInitialized'] = 'true';

    btn.addEventListener('click', async () => {
        btn.disabled = true;

        try {
            const response = await fetch('/offers/sms-opt-in', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCSRFToken()
                }
            });

            if (response.ok) {
                const html = await response.text();
                const widget = btn.closest<HTMLElement>(SMS_ALERT_SELECTOR);
                if (widget) {
                    widget.outerHTML = html;
                }
            } else {
                btn.disabled = false;
            }
        } catch {
            btn.disabled = false;
        }
    });
}

/**
 * Initializes click handlers for CLO card add buttons
 */
function initCloCards(): void {
    document
        .querySelectorAll<HTMLButtonElement>(CLO_ADD_BTN_SELECTOR)
        .forEach(btn => {
            // Skip if already initialized
            if (btn.dataset['cloInitialized']) return;
            btn.dataset['cloInitialized'] = 'true';

            btn.addEventListener('click', event => {
                event.preventDefault();
                const card = btn.closest<HTMLElement>(CLO_CARD_SELECTOR);
                if (card) {
                    clipOffer(card, btn);
                }
            });
        });

    initSMSOptIn();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCloCards);
} else {
    initCloCards();
}

// Re-initialize after HTMX swaps (for dynamic content)
document.body.addEventListener('htmx:afterSwap', () => {
    initCloCards();
});

// Export for potential use elsewhere
export { initCloCards, clipOffer };
